import { useEffect, useLayoutEffect, useRef, useState } from "react";
import GestaltLogo from "./GestaltLogo";
import { primaryCtaClass } from "../utils/ctaClasses";
import { scheduleDemoFormScroll } from "../utils/scrollDemoFormIntoView";
import { MOBILE_LG_QUERY, useMediaQuery } from "../utils/useMediaQuery";

const SCROLL_DOCK_THRESHOLD = 16;

const dockedShellClass =
  "pointer-events-auto max-w-none rounded-none border-b border-slate-200/30 bg-gradient-to-r from-white/70 via-white/55 to-blue-50/45 shadow-lg shadow-slate-900/10 backdrop-blur-xl backdrop-saturate-150 max-lg:border-slate-200/80 max-lg:bg-white max-lg:shadow-md max-lg:backdrop-blur-none";

const floatingShellClass =
  "pointer-events-auto max-w-7xl rounded-2xl border border-white/20 bg-white/10 shadow-xl shadow-black/20 ring-1 ring-white/25 backdrop-blur-xl backdrop-saturate-150";

const navItems = [
  { label: "The Challenge", href: "#solutions" },
  { label: "Product", href: "#product" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Security", href: "#security" },
  { label: "Use cases", href: "#use-cases" },
  { label: "Core system", href: "#core-system" },
  { label: "Social proof", href: "#resources" },
  { label: "About", href: "#company" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const navShellRef = useRef(null);
  const [mobileMenuTop, setMobileMenuTop] = useState(72);
  const isMobile = useMediaQuery(MOBILE_LG_QUERY);
  const [isDocked, setIsDocked] = useState(
    () =>
      typeof window !== "undefined" &&
      !window.matchMedia(MOBILE_LG_QUERY).matches &&
      window.scrollY > SCROLL_DOCK_THRESHOLD
  );

  useEffect(() => {
    const syncDocked = () => {
      if (window.matchMedia(MOBILE_LG_QUERY).matches) return;
      setIsDocked(window.scrollY > SCROLL_DOCK_THRESHOLD);
    };

    syncDocked();
    window.addEventListener("scroll", syncDocked, { passive: true });
    window.addEventListener("resize", syncDocked, { passive: true });

    const mediaQuery = window.matchMedia(MOBILE_LG_QUERY);
    mediaQuery.addEventListener("change", syncDocked);

    return () => {
      window.removeEventListener("scroll", syncDocked);
      window.removeEventListener("resize", syncDocked);
      mediaQuery.removeEventListener("change", syncDocked);
    };
  }, []);

  const useFullBar = isDocked || isMobile;

  useLayoutEffect(() => {
    const measureMenuTop = () => {
      if (!navShellRef.current) return;
      setMobileMenuTop(navShellRef.current.getBoundingClientRect().bottom);
    };

    measureMenuTop();
    window.addEventListener("resize", measureMenuTop, { passive: true });
    return () => window.removeEventListener("resize", measureMenuTop);
  }, [isOpen, isMobile, useFullBar]);

  useEffect(() => {
    if (!isMobile || !isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, isOpen]);

  const scrollDemoFormIntoView = (event) => {
    event.preventDefault();
    setIsOpen(false);

    scheduleDemoFormScroll({
      navElement: navShellRef.current,
      updateHash: true,
    });
  };

  const mobileMenuPanelClass = useFullBar
    ? "border-slate-200 bg-white text-secondary"
    : "border-white/15 bg-slate-950/95 text-white";

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 w-full bg-transparent transition-[padding] duration-300 ease-out max-lg:transition-none ${
        useFullBar
          ? "px-0 pt-0"
          : "pointer-events-none px-3 pt-3 sm:px-4 sm:pt-4 md:px-6 md:pt-5"
      }`}
    >
      <div
        ref={navShellRef}
        className={`mx-auto w-full transition-[border-radius,box-shadow,border-color,max-width,background-color,backdrop-filter] duration-300 ease-out max-lg:overflow-visible lg:overflow-hidden max-lg:transition-none ${
          useFullBar ? dockedShellClass : floatingShellClass
        }`}
      >
        <div className="mx-auto flex min-h-[4.5rem] w-full max-w-7xl min-w-0 items-center justify-between gap-2 px-4 py-3 max-lg:max-w-none md:min-h-[4.875rem] md:gap-4 md:px-6 md:py-4">
          <a
            href="#home"
            className="group/logo -mx-1 inline-flex min-w-0 shrink items-center rounded-xl px-1 py-0.5 transition-[transform,filter] duration-200 ease-out hover:scale-[1.02] active:scale-[0.99] max-lg:transition-none max-lg:hover:scale-100 max-lg:active:scale-100"
            aria-label="Gestalt home"
          >
            <span className="logo-mark inline-flex max-w-full items-center">
              <GestaltLogo
                variant={useFullBar ? "default" : "white"}
                className="h-12 w-auto max-w-full max-lg:transition-none md:h-14 lg:h-[63px] lg:transition-opacity lg:duration-300"
              />
            </span>
          </a>

          <div className="hidden flex-wrap justify-end gap-x-5 gap-y-2 text-sm lg:flex xl:gap-x-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`transition hover:text-accent-hover max-lg:transition-none ${
                  useFullBar ? "text-secondary hover:text-accent-hover" : "text-white/90 hover:text-white"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-2 md:gap-4">
            <a
              href="#demo-form"
              className={`${primaryCtaClass} hidden px-5 py-2.5 text-sm font-semibold lg:inline-flex`}
              onClick={scrollDemoFormIntoView}
            >
              Connect with us
            </a>
            <button
              type="button"
              className={`shrink-0 whitespace-nowrap rounded-lg border px-3 py-2 text-sm transition hover:scale-105 max-lg:transition-none max-lg:hover:scale-100 lg:hidden ${
                useFullBar
                  ? "border-slate-200 text-secondary hover:border-slate-300 hover:shadow-lg"
                  : "border-white/30 text-white/90 hover:border-white/50 hover:text-white"
              }`}
              onClick={() => setIsOpen((prev) => !prev)}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              Menu
            </button>
          </div>
        </div>
      </div>

      {isOpen && isMobile && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-slate-950/20 lg:hidden"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
          />
          <nav
            aria-label="Mobile"
            className={`fixed inset-x-0 z-50 box-border max-h-[calc(100dvh-4rem)] overflow-y-auto border-t px-4 pb-6 pt-4 shadow-lg lg:hidden ${mobileMenuPanelClass}`}
            style={{ top: mobileMenuTop }}
          >
            <div className="flex w-full min-w-0 flex-col gap-3 text-sm">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`transition hover:text-accent-hover max-lg:transition-none ${
                    useFullBar ? "text-secondary" : "text-white/90 hover:text-white"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#demo-form"
                className={`${primaryCtaClass} mt-2 box-border w-full max-w-full px-5 py-3 text-center text-sm font-semibold max-lg:transition-none max-lg:hover:scale-100`}
                onClick={scrollDemoFormIntoView}
              >
                Connect with us
              </a>
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
